import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { ChevronLeft, ChevronRight, Lock, Unlock, Calendar, UserPlus, Users, X, Plus } from "lucide-react";

const REASON_OPTIONS = [
  { value: "Travaux", label: "🔧 Travaux" },
  { value: "Repos", label: "😴 Repos" },
  { value: "Session privée", label: "🔒 Session privée" },
  { value: "Autre", label: "📝 Autre" },
];

const PLANS = {
  "coaching_1sem_monthly": { name: "Coaching 1 séance/sem" },
  "coaching_2sem_monthly": { name: "Coaching 2 séances/sem" },
  "coaching_3sem_monthly": { name: "Coaching 3 séances/sem" },
  "libre_1sem_monthly": { name: "Accès libre 1 séance/sem" },
  "libre_2sem_monthly": { name: "Accès libre 2 séances/sem" },
  "libre_3sem_monthly": { name: "Accès libre 3 séances/sem" },
};

export default function ScheduleManager() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [calendar, setCalendar] = useState({ week_start: "", week_end: "", slots: [] });
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState("Travaux");
  const [actionLoading, setActionLoading] = useState(false);
  
  // New states for participant management
  const [slotBookings, setSlotBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddTrial, setShowAddTrial] = useState(false);
  const [trialForm, setTrialForm] = useState({ first_name: "", last_name: "", phone_number: "" });
  const [confirmDialog, setConfirmDialog] = useState(null); // { type: 'delete' | 'capacity', data: {} }
  const [customCapacity, setCustomCapacity] = useState(""); // Custom capacity input
  
  // New state for day navigation
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/calendar/week?week_offset=${weekOffset}`);
      setCalendar(res.data);
      
      // Initialize selectedDay to current day if not set or if it doesn't exist in new week
      if (!selectedDay || !res.data.slots.some(slot => slot.day === selectedDay)) {
        const currentDayOfWeek = new Date().getDay(); // 0=Dimanche, 1=Lundi, etc.
        const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
        
        // If weekOffset is 0 (current week), try to select today
        if (weekOffset === 0) {
          const todayIndex = currentDayOfWeek === 0 ? -1 : currentDayOfWeek - 1; // Convert to 0=Lundi
          if (todayIndex >= 0 && todayIndex < days.length) {
            const todayName = days[todayIndex];
            // Check if today has slots
            if (res.data.slots.some(slot => slot.day === todayName)) {
              setSelectedDay(todayName);
              return;
            }
          }
        }
        
        // Otherwise, select the first day that has slots
        const firstDayWithSlots = res.data.slots[0]?.day;
        setSelectedDay(firstDayWithSlots || "Lundi");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCapacity = async () => {
    if (!selectedSlot) return;
    
    const newCapacity = parseInt(customCapacity);
    if (isNaN(newCapacity) || newCapacity < 1) {
      toast.error("La capacité doit être un nombre supérieur à 0");
      return;
    }
    
    setActionLoading(true);
    try {
      await api.put(`/admin/slot-capacity?slot_id=${selectedSlot.slot_id}`, {
        max_capacity: newCapacity
      });
      toast.success(`Capacité mise à jour : ${newCapacity} places`);
      await fetchCalendar();
      // Update local state
      setSelectedSlot({ ...selectedSlot, max_capacity: newCapacity });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur lors de la mise à jour");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendar();
  }, [weekOffset]);

  const handleManageSlot = async (slot) => {
    setSelectedSlot(slot);
    setBlockReason("Travaux");
    setShowAddMember(false);
    setShowAddTrial(false);
    setTrialForm({ first_name: "", last_name: "", phone_number: "" });
    setSelectedMember("");
    
    // Initialize custom capacity
    setCustomCapacity(slot.max_capacity?.toString() || "");
    
    setManageModalOpen(true);
    
    // Load bookings for this slot
    await loadSlotBookings(slot.slot_id);
    
    // Load all members if not loaded
    if (allMembers.length === 0) {
      await loadAllMembers();
    }
  };

  const loadSlotBookings = async (slotId) => {
    setLoadingBookings(true);
    try {
      const res = await api.get(`/admin/slots/${slotId}/bookings`);
      setSlotBookings(res.data.bookings || []);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur de chargement des participants");
    } finally {
      setLoadingBookings(false);
    }
  };

  const loadAllMembers = async () => {
    try {
      const res = await api.get("/admin/members");
      setAllMembers(res.data || []);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur de chargement des membres");
    }
  };

  const handleAddMember = async () => {
    if (!selectedMember || !selectedSlot) return;
    
    setActionLoading(true);
    try {
      const res = await api.post("/admin/bookings/member", {
        user_id: selectedMember,
        slot_start: selectedSlot.slot_start,
        family: selectedSlot.family,
      });
      
      // Check if booking was created successfully
      if (res.data.ok) {
        if (res.data.at_capacity) {
          toast.warning("Membre ajouté (créneau complet)");
        } else {
          toast.success(res.data.message || "Membre ajouté avec succès");
        }
        
        // Reset form and refresh
        setSelectedMember("");
        setShowAddMember(false);
        await loadSlotBookings(selectedSlot.slot_id);
        await fetchCalendar();
      } else {
        // Should not happen if backend is correct, but handle anyway
        toast.error(res.data.message || "Erreur lors de l'ajout");
      }
    } catch (err) {
      const errorMsg = formatErr(err.response?.data?.detail) || "Erreur lors de l'ajout";
      toast.error(errorMsg);
      
      // Refresh anyway in case booking was created despite error
      try {
        await loadSlotBookings(selectedSlot.slot_id);
        await fetchCalendar();
      } catch (refreshErr) {
        console.error("Failed to refresh after error:", refreshErr);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddTrial = async () => {
    if (!trialForm.first_name || !trialForm.last_name || !trialForm.phone_number || !selectedSlot) {
      toast.error("Tous les champs sont obligatoires");
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await api.post("/admin/bookings/trial-guest", {
        first_name: trialForm.first_name,
        last_name: trialForm.last_name,
        phone_number: trialForm.phone_number,
        slot_start: selectedSlot.slot_start,
        family: selectedSlot.family,
      });
      
      // Check if booking was created successfully
      if (res.data.ok) {
        if (res.data.at_capacity) {
          toast.warning("Essai ajouté (créneau complet)");
        } else {
          toast.success(res.data.message || "Essai ajouté avec succès");
        }
        
        // Reset form and refresh
        setTrialForm({ first_name: "", last_name: "", phone_number: "" });
        setShowAddTrial(false);
        await loadSlotBookings(selectedSlot.slot_id);
        await fetchCalendar();
      } else {
        // Should not happen if backend is correct, but handle anyway
        toast.error(res.data.message || "Erreur lors de l'ajout de l'essai");
      }
    } catch (err) {
      const errorMsg = formatErr(err.response?.data?.detail) || "Erreur lors de l'ajout de l'essai";
      toast.error(errorMsg);
      
      // Refresh anyway in case booking was created despite error
      try {
        await loadSlotBookings(selectedSlot.slot_id);
        await fetchCalendar();
      } catch (refreshErr) {
        console.error("Failed to refresh after error:", refreshErr);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBooking = (booking) => {
    setConfirmDialog({
      type: "delete",
      data: { booking },
      message: `Êtes-vous sûr de vouloir retirer ${booking.user_name} de ce créneau ?`,
    });
  };

  const confirmDeleteBooking = async () => {
    if (!confirmDialog || confirmDialog.type !== "delete") return;
    
    const booking = confirmDialog.data.booking;
    setActionLoading(true);
    try {
      await api.delete(`/bookings/${booking.id}`);
      toast.success("Inscription supprimée");
      setConfirmDialog(null);
      await loadSlotBookings(selectedSlot.slot_id);
      await fetchCalendar();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur lors de la suppression");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlockSlot = async () => {
    if (!selectedSlot) return;
    
    setActionLoading(true);
    try {
      await api.post("/admin/slots/block", {
        slot_start: selectedSlot.slot_start,
        family: selectedSlot.family,
        reason: blockReason,
      });
      toast.success(`Créneau bloqué : ${blockReason}`);
      setManageModalOpen(false);
      fetchCalendar();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur de blocage");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockSlot = async () => {
    if (!selectedSlot) return;
    
    setActionLoading(true);
    try {
      await api.post("/admin/slots/unblock", {
        slot_start: selectedSlot.slot_start,
        family: selectedSlot.family,
      });
      toast.success("Créneau débloqué");
      setManageModalOpen(false);
      fetchCalendar();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur de déblocage");
    } finally {
      setActionLoading(false);
    }
  };

  const getSlotColor = (slot) => {
    // Design noir avec liseré de couleur
    if (slot.family === "coaching") {
      // Coaching (semaine + samedi) : liseré bleu
      return {
        bg: "bg-black",
        border: "border-blue-500",
        borderWidth: "border-2",
        text: "text-blue-400",
        badge: "bg-blue-950 text-blue-300 border border-blue-700"
      };
    }
    // Libre (semaine + samedi) : liseré gris
    return {
      bg: "bg-black",
      border: "border-stone-400",
      borderWidth: "border-2",
      text: "text-stone-200",
      badge: "bg-stone-900 text-stone-300 border border-stone-700"
    };
  };

  const getCapacityText = (slot) => {
    // Utilise max_capacity du backend
    const max = slot.max_capacity || 1;
    const used = slot.bookings_count || 0;
    
    // Affichage uniforme : Places : X/Y
    return `Places : ${used}/${max}`;
  };

  const getStatusBadge = (slot) => {
    const max = slot.max_capacity || 1;
    const used = slot.bookings_count || 0;
    
    if (slot.status === "blocked") {
      return (
        <span className="rounded-md bg-red-950 border border-red-700 px-2 py-1 text-xs font-semibold text-red-300">
          🔒 Bloqué
        </span>
      );
    }
    
    if (used === 0) {
      return (
        <span className="rounded-md bg-green-950 border border-green-700 px-2 py-1 text-xs font-semibold text-green-300">
          Disponible
        </span>
      );
    }
    
    // Au moins une personne inscrite
    return (
      <span className="rounded-md bg-orange-950 border border-orange-700 px-2 py-1 text-xs font-semibold text-orange-300">
        Occupé : {used}/{max}
      </span>
    );
  };

  const groupedSlots = calendar.slots.reduce((acc, slot) => {
    if (!acc[slot.day]) acc[slot.day] = [];
    acc[slot.day].push(slot);
    return acc;
  }, {});

  const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  
  // Navigation helpers
  const navigateDay = (direction) => {
    const currentIndex = days.indexOf(selectedDay);
    if (direction === 'prev' && currentIndex > 0) {
      setSelectedDay(days[currentIndex - 1]);
    } else if (direction === 'next' && currentIndex < days.length - 1) {
      setSelectedDay(days[currentIndex + 1]);
    }
  };
  
  // Get short day name for mobile
  const getShortDay = (day, date) => {
    const dayMap = {
      "Lundi": "Lun",
      "Mardi": "Mar",
      "Mercredi": "Mer",
      "Jeudi": "Jeu",
      "Vendredi": "Ven",
      "Samedi": "Sam"
    };
    return dayMap[day] || day.slice(0, 3);
  };
  
  // Check if a day has bookings (for indicator)
  const dayHasBookings = (day) => {
    const slots = groupedSlots[day] || [];
    return slots.some(slot => slot.bookings_count > 0);
  };

  return (
    <div className="space-y-6" style={{ backgroundColor: '#000000' }}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion du Planning</h2>
          <p className="mt-1 text-sm text-stone-400">
            Bloquez ou débloquez des créneaux pour gérer votre disponibilité
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekOffset(weekOffset - 1)}
            disabled={loading}
            className="border-stone-700 bg-stone-900 text-white hover:bg-stone-800"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 rounded-lg border border-stone-700 bg-stone-900 px-4 py-2">
            <Calendar className="h-4 w-4 text-stone-400" />
            <span className="text-sm font-medium text-white">
              {calendar.week_start && new Date(calendar.week_start).toLocaleDateString("fr-FR")} -{" "}
              {calendar.week_end && new Date(calendar.week_end).toLocaleDateString("fr-FR")}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekOffset(weekOffset + 1)}
            disabled={loading}
            className="border-stone-700 bg-stone-900 text-white hover:bg-stone-800"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setWeekOffset(0)}
            disabled={loading || weekOffset === 0}
            className="text-white hover:bg-stone-800"
          >
            Aujourd&apos;hui
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 bg-stone-950 rounded-xl">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-stone-700 border-t-stone-400"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Horizontal Day Navigation */}
          <div className="flex items-center gap-2 bg-stone-950 p-4 rounded-xl overflow-x-auto" style={{ backgroundColor: '#0a0a0a' }}>
            {/* Previous day button */}
            <button
              onClick={() => navigateDay('prev')}
              disabled={days.indexOf(selectedDay) === 0}
              className="flex-shrink-0 p-2 rounded-lg border border-stone-700 bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Jour précédent"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            {/* Day buttons */}
            <div className="flex gap-2 flex-1 overflow-x-auto pb-1">
              {days.map((day) => {
                const isSelected = day === selectedDay;
                const hasBookings = dayHasBookings(day);
                const hasSlots = (groupedSlots[day] || []).length > 0;
                
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDay(day)}
                    disabled={!hasSlots}
                    className={`relative flex-shrink-0 px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                      isSelected
                        ? 'bg-white text-stone-900 shadow-lg'
                        : hasSlots
                        ? 'bg-stone-900 text-stone-300 hover:bg-stone-800 border border-stone-700'
                        : 'bg-stone-950 text-stone-600 cursor-not-allowed opacity-50'
                    }`}
                  >
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{getShortDay(day)}</span>
                    {hasBookings && (
                      <span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ${isSelected ? 'bg-blue-500' : 'bg-orange-500'}`} title="Réservations actives"></span>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Next day button */}
            <button
              onClick={() => navigateDay('next')}
              disabled={days.indexOf(selectedDay) === days.length - 1}
              className="flex-shrink-0 p-2 rounded-lg border border-stone-700 bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Jour suivant"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          {/* Selected Day Content */}
          <div className="bg-stone-950 p-6 rounded-xl" style={{ backgroundColor: '#0a0a0a' }}>
            <h3 className="mb-4 text-2xl font-bold text-white">{selectedDay}</h3>
            
            {(groupedSlots[selectedDay] || []).length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucun créneau disponible pour {selectedDay}</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {(groupedSlots[selectedDay] || []).map((slot) => {
                  const colors = getSlotColor(slot);
                  return (
                    <div
                      key={slot.slot_id}
                      style={{ backgroundColor: '#000000' }}
                      className={`rounded-lg ${colors.borderWidth} ${colors.border} p-4 transition-all hover:shadow-lg hover:shadow-stone-900/50`}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <div>
                          <span className={`text-sm font-semibold ${colors.text}`}>
                            {slot.time}
                          </span>
                          <span className="ml-2 text-xs text-stone-500">
                            {slot.duration} min
                          </span>
                        </div>
                        {getStatusBadge(slot)}
                      </div>
                      
                      {/* Affichage de la capacité */}
                      <div className={`mb-2 text-xs font-medium ${colors.text}`}>
                        {getCapacityText(slot)}
                      </div>
                      
                      {slot.status === "blocked" && slot.reason && (
                        <p className="mb-2 text-xs text-red-400 font-medium">
                          🔒 Motif : {slot.reason}
                        </p>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className={`w-full text-xs ${colors.badge} border-0`}
                        onClick={() => handleManageSlot(slot)}
                      >
                        Gérer le créneau
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manage Slot Modal */}
      <Dialog open={manageModalOpen} onOpenChange={setManageModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gérer le créneau</DialogTitle>
            <DialogDescription>
              {selectedSlot && (
                <>
                  {selectedSlot.day} {selectedSlot.time} - {selectedSlot.duration} min ({selectedSlot.family})
                  <br />
                  <span className="mt-1 inline-block">
                    {selectedSlot.bookings_count}/{selectedSlot.max_capacity} places occupées
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Section 1: Liste des participants */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Participants inscrits ({slotBookings.length})
                </h3>
              </div>
              
              {loadingBookings ? (
                <p className="text-sm text-gray-500">Chargement...</p>
              ) : slotBookings.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun participant inscrit</p>
              ) : (
                <div className="space-y-2">
                  {slotBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">{booking.user_name || "Invité"}</p>
                        <p className="text-xs text-gray-600">
                          {booking.user_email || booking.guest_phone}
                          {booking.booking_type === "trial_guest" && " • Essai"}
                          {booking.is_trial && booking.booking_type !== "trial_guest" && " • Essai"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteBooking(booking)}
                        disabled={actionLoading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Section 2: Ajouter un membre */}
            {selectedSlot?.status !== "blocked" && (
              <div className="border rounded-lg p-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="mb-3 w-full"
                  onClick={() => setShowAddMember(!showAddMember)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un membre
                </Button>
                
                {showAddMember && (
                  <div className="space-y-3 pt-2 border-t">
                    <div>
                      <Label>Sélectionner un membre</Label>
                      <Select value={selectedMember} onValueChange={setSelectedMember}>
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Choisir un membre" />
                        </SelectTrigger>
                        <SelectContent>
                          {allMembers.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.name} ({member.email}) - {PLANS[member.active_plan]?.name || "Aucun forfait"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleAddMember}
                      disabled={!selectedMember || actionLoading}
                      className="w-full"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      {actionLoading ? "Ajout..." : "Ajouter"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Section 3: Ajouter un essai */}
            {selectedSlot?.status !== "blocked" && (
              <div className="border rounded-lg p-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="mb-3 w-full"
                  onClick={() => setShowAddTrial(!showAddTrial)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une séance d&apos;essai
                </Button>
                
                {showAddTrial && (
                  <div className="space-y-3 pt-2 border-t">
                    <div>
                      <Label>Prénom *</Label>
                      <Input
                        value={trialForm.first_name}
                        onChange={(e) => setTrialForm({ ...trialForm, first_name: e.target.value })}
                        placeholder="Prénom"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label>Nom *</Label>
                      <Input
                        value={trialForm.last_name}
                        onChange={(e) => setTrialForm({ ...trialForm, last_name: e.target.value })}
                        placeholder="Nom"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label>Téléphone *</Label>
                      <Input
                        value={trialForm.phone_number}
                        onChange={(e) => setTrialForm({ ...trialForm, phone_number: e.target.value })}
                        placeholder="06 12 34 56 78"
                        className="mt-1.5"
                      />
                    </div>
                    <Button
                      onClick={handleAddTrial}
                      disabled={actionLoading}
                      className="w-full"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      {actionLoading ? "Ajout..." : "Ajouter l'essai"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Section 3.5: Capacité maximale */}
            {selectedSlot?.status !== "blocked" && (
              <div className="border rounded-lg p-4 bg-blue-50/30">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="capacity" className="text-sm font-semibold">
                      Capacité maximale
                    </Label>
                    {selectedSlot?.default_capacity && (
                      <span className="text-xs text-gray-500">
                        Par défaut : {selectedSlot.default_capacity}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="capacity"
                      type="number"
                      min="1"
                      max="20"
                      value={customCapacity}
                      onChange={(e) => setCustomCapacity(e.target.value)}
                      placeholder={selectedSlot?.default_capacity?.toString() || "3"}
                      className="flex-1"
                    />
                    <Button
                      onClick={handleUpdateCapacity}
                      disabled={actionLoading || !customCapacity || parseInt(customCapacity) === selectedSlot?.max_capacity}
                      size="sm"
                    >
                      {actionLoading ? "..." : "Appliquer"}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-600">
                    Définissez le nombre maximal de places pour ce créneau spécifique.
                  </p>
                </div>
              </div>
            )}

            {/* Section 4: Bloquer/Débloquer */}
            <div className="border rounded-lg p-4">
              {selectedSlot?.status === "blocked" ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-red-900">
                    🔒 Ce créneau est bloqué
                  </p>
                  {selectedSlot.reason && (
                    <p className="text-xs text-red-700">Motif : {selectedSlot.reason}</p>
                  )}
                  <Button
                    onClick={handleUnblockSlot}
                    disabled={actionLoading}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Unlock className="mr-2 h-4 w-4" />
                    {actionLoading ? "Déblocage..." : "Débloquer ce créneau"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Label htmlFor="block-reason">Bloquer le créneau</Label>
                  <Select value={blockReason} onValueChange={setBlockReason}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir un motif" />
                    </SelectTrigger>
                    <SelectContent>
                      {REASON_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleBlockSlot}
                    disabled={actionLoading}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    {actionLoading ? "Blocage..." : "Bloquer ce créneau"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setManageModalOpen(false)}
              disabled={actionLoading}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirmation</DialogTitle>
            <DialogDescription>
              {confirmDialog?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmDialog(null)}
              disabled={actionLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={confirmDeleteBooking}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? "Suppression..." : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
